import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Megaphone, Zap, Target, Clock3, Database, RefreshCw, ShieldCheck, Filter, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PageHeader, SectionCard, Stat } from "@/components/exec/Primitives";
import { RecommendationCard, PromotionCard, InsightsBody, InsightsHeaderActions, ExecutiveSummaryText, useInsights } from "@/components/exec/Insights";
import { useScope } from "@/lib/scope";
import { useApp } from "@/lib/app";
import { api, errorMessage } from "@/lib/api";
import { fmtDate, fmtTimeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "high", label: "High impact" },
  { id: "medium", label: "Medium" },
  { id: "low", label: "Low" },
  { id: "cross_sell", label: "Cross-sell" },
];

export default function AiGrowth() {
  const { params } = useScope();
  const { isAdmin, scopes } = useApp();
  const ins = useInsights("growth", params);
  const [filter, setFilter] = useState("all");
  const [nightlyBusy, setNightlyBusy] = useState(false);
  const state = ins.data;
  const scopeName = scopes?.find((s) => s.id === params.scope)?.name || "All stores";

  useEffect(() => {
    document.title = "Zapin | AI Growth";
  }, []);

  const recs = useMemo(() => {
    const list = state?.data?.recommendations || [];
    if (filter === "all") return list;
    if (filter === "cross_sell") return list.filter((r) => r.cross_sell || r.category === "cross_sell");
    return list.filter((r) => r.impact === filter);
  }, [state, filter]);

  const runNightly = async () => {
    setNightlyBusy(true);
    try {
      const res = await api.runNightlyInsights();
      toast.success("Nightly AI batch started", { description: `${res.views} standard views are being refreshed in the background.` });
    } catch (e) {
      toast.error(errorMessage(e, "Could not start the batch"));
    } finally {
      setNightlyBusy(false);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8" data-testid="ai-growth-page">
      <PageHeader
        eyebrow={`AI growth · ${scopeName}`}
        title="Where the next revenue comes from"
        subtitle={`Prioritised moves and ready-to-brief promotions, built only from the evidence in the selected scope and period${state?.period ? ` (${state.period.label})` : ""}. Standard views refresh nightly at 06:00 MYT; any view can be generated on demand.`}
        testId="ai-growth-header"
        actions={
          <>
            <InsightsHeaderActions state={{ ...state, starting: ins.starting }} onGenerate={ins.generate} />
            {isAdmin && (
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={runNightly} disabled={nightlyBusy} data-testid="ai-growth-run-nightly">
                {nightlyBusy ? <RefreshCw size={13} className="animate-spin" /> : <Clock3 size={13} />} Run nightly batch now
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="ai-growth-evidence-strip">
        <Stat label="Scope" value={scopeName} sub={state?.scope_id} testId="ai-growth-scope" />
        <Stat label="Window" value={state?.period ? `${fmtDate(state.period.start)} – ${fmtDate(state.period.end)}` : "—"} sub={state?.period?.compare_label} testId="ai-growth-window" />
        <Stat label="Evidence" value={state?.has_sales ? "Counts + POS" : state?.has_counts ? "Counts only" : "—"} sub={state?.has_sales ? "sales joined to footfall" : "no POS in this window"} testId="ai-growth-evidence" />
        <Stat label="Last generated" value={state?.generated_at ? fmtTimeAgo(state.generated_at) : "never"} sub={state?.trigger === "nightly" ? "nightly batch" : state?.model ? state.model.split("/")[1] : ""} testId="ai-growth-generated" />
      </div>

      <InsightsBody
        state={ins.isLoading ? { isLoading: true } : state}
        kind="growth"
        emptyHint="No growth plan for this view yet. Press Generate to have the strategist read footfall, calendar and POS patterns for this scope and period - it takes about 30 seconds."
        renderReady={(data) => (
          <div className="space-y-6 md:space-y-8">
            <ExecutiveSummaryText text={data.executive_summary} watchlist={data.watchlist} />

            <section data-testid="ai-growth-recommendations">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
                <div>
                  <div className="eyebrow">Prioritised recommendations</div>
                  <h2 className="font-display text-lg md:text-xl inline-flex items-center gap-2"><Target size={17} className="text-gold" /> {data.recommendations?.length || 0} moves, highest impact first</h2>
                </div>
                <ToggleGroup type="single" value={filter} onValueChange={(v) => v && setFilter(v)} className="justify-start flex-wrap" data-testid="ai-growth-filter">
                  {FILTERS.map((f) => (
                    <ToggleGroupItem key={f.id} value={f.id} className="h-8 text-xs data-[state=on]:bg-gold/15 data-[state=on]:text-foreground" data-testid={`ai-growth-filter-${f.id}`}>{f.id === "cross_sell" && <Zap size={11} className="mr-1" />}{f.label}</ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
              {recs.length ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4" data-testid="ai-growth-recommendation-grid">
                  {recs.map((r, i) => <RecommendationCard key={r.id || i} rec={r} index={i} />)}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border/70 bg-muted/10 p-6 text-sm text-muted-foreground inline-flex items-center gap-2" data-testid="ai-growth-filter-empty"><Filter size={14} /> No recommendations match this filter.</div>
              )}
            </section>

            <section data-testid="ai-growth-promotions">
              <div className="mb-4">
                <div className="eyebrow">Promotion drafts</div>
                <h2 className="font-display text-lg md:text-xl inline-flex items-center gap-2"><Megaphone size={17} className="text-gold" /> Ready to brief a store manager</h2>
                <p className="text-xs text-muted-foreground mt-1">Each draft implements one recommendation above. Copy the brief and send it on; adjust mechanics to local duty-free rules.</p>
              </div>
              {data.promotions?.length ? (
                <div className="grid grid-cols-1 gap-3 md:gap-4" data-testid="ai-growth-promotion-list">
                  {data.promotions.map((p, i) => <PromotionCard key={p.id || i} promo={p} index={i} />)}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border/70 bg-muted/10 p-6 text-sm text-muted-foreground" data-testid="ai-growth-promotions-empty">No promotion drafts were produced for this view.</div>
              )}
            </section>
          </div>
        )}
      />

      <SectionCard title="How this is built" subtitle="So the board can trust it." testId="ai-growth-method">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-3"><span className="h-8 w-8 rounded-lg bg-gold/12 text-gold flex items-center justify-center shrink-0"><Database size={15} /></span><div><div className="font-medium">Evidence pack first</div><p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Footfall, walk-in, demographics, store shares, calendar lift, payday effect and - where uploaded - POS revenue, conversion, basket and mix are computed deterministically for the exact scope and window.</p></div></div>
          <div className="flex items-start gap-3"><span className="h-8 w-8 rounded-lg bg-gold/12 text-gold flex items-center justify-center shrink-0"><Sparkles size={15} /></span><div><div className="font-medium">Strategist reads only the pack</div><p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">The model may not invent numbers. Every recommendation cites its evidence, names the KPI it moves and gives an expected range, so it can be checked against the charts.</p></div></div>
          <div className="flex items-start gap-3"><span className="h-8 w-8 rounded-lg bg-gold/12 text-gold flex items-center justify-center shrink-0"><ShieldCheck size={15} /></span><div><div className="font-medium">Cached and fingerprinted</div><p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Results are stored per scope and window. When new counts or receipts arrive the view is flagged as moved and can be regenerated; the nightly batch keeps the standard views current.</p></div></div>
        </div>
        {state?.has_sales === false && (
          <div className="mt-4 rounded-lg border border-dashed border-border/70 bg-muted/10 p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="ai-growth-no-pos-note">
            <Receipt size={13} className="text-gold mt-0.5 shrink-0" />
            <span>This view has no POS receipts, so recommendations lean on footfall and calendar patterns. Upload POS for the stores in scope to unlock basket, conversion and product-mix moves.</span>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
